import { useCallback, useEffect, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import EventImageSlot from "@/components/feature/EventImageSlot";
import {
  deleteEvent,
  fetchAttendees,
  fetchEvents,
  isUpcoming,
  pickImage,
  KIND_META,
  saveEvent,
  type Attendee,
  type CalEvent,
  type EventAudience,
  type EventKind,
} from "@/services/events";

const inputCls = "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";

type Form = {
  title: string;
  description: string;
  kind: EventKind;
  startsLocal: string;
  duration: string;
  host: string;
  imageUrl: string;
  imageVerticalUrl: string;
  imageSquareUrl: string;
  joinUrl: string;
  recordingUrl: string;
  recordingMinutes: string;
  capacity: string;
  audience: EventAudience;
  active: boolean;
  cancelled: boolean;
};

const pad = (n: number) => String(n).padStart(2, "0");
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = (): Form => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(19, 0, 0, 0);
  return {
    title: "",
    description: "",
    kind: "masterclass",
    startsLocal: toLocalInput(d.toISOString()),
    duration: "60",
    host: "",
    imageUrl: "",
    imageVerticalUrl: "",
    imageSquareUrl: "",
    joinUrl: "",
    recordingUrl: "",
    recordingMinutes: "",
    capacity: "",
    audience: "todos",
    active: true,
    cancelled: false,
  };
};

const isUrl = (v: string) => {
  try {
    const u = new URL(v);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

export default function AdminEventos() {
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [modal, setModal] = useState<{ editing: CalEvent | null } | null>(null);
  const [form, setForm] = useState<Form>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [attendees, setAttendees] = useState<{ event: CalEvent; list: Attendee[] | null } | null>(null);

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  const load = useCallback(async () => setEvents((await fetchEvents()).slice().sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime())), []);

  useEffect(() => {
    let active = true;
    load()
      .catch((e) => active && setError(e instanceof Error ? e.message : "No se pudieron cargar los eventos."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const open = (e: CalEvent | null) => {
    setFormError(null);
    setForm(
      e
        ? {
            title: e.title,
            description: e.description ?? "",
            kind: e.kind,
            startsLocal: toLocalInput(e.startsAt),
            duration: String(e.durationMinutes),
            host: e.hostName ?? "",
            imageUrl: e.imageUrl ?? "",
            imageVerticalUrl: e.imageVerticalUrl ?? "",
            imageSquareUrl: e.imageSquareUrl ?? "",
            joinUrl: e.joinUrl ?? "",
            recordingUrl: e.recordingUrl ?? "",
            recordingMinutes: e.recordingSeconds ? String(Math.round(e.recordingSeconds / 60)) : "",
            capacity: e.capacity ? String(e.capacity) : "",
            audience: e.audience,
            active: e.active,
            cancelled: e.cancelled,
          }
        : emptyForm(),
    );
    setModal({ editing: e });
  };

  const submit = async () => {
    if (!modal) return;
    const title = form.title.trim();
    const duration = Number(form.duration);
    if (title.length < 3) return setFormError("El título debe tener al menos 3 caracteres.");
    if (!form.startsLocal || Number.isNaN(new Date(form.startsLocal).getTime())) return setFormError("Elige la fecha y hora del evento.");
    if (!Number.isInteger(duration) || duration < 5 || duration > 600) return setFormError("La duración debe estar entre 5 y 600 minutos.");
    for (const [label, v] of [
      ["El enlace de la reunión", form.joinUrl],
      ["El enlace de la grabación", form.recordingUrl],
    ] as const) {
      if (v.trim() && !isUrl(v.trim())) return setFormError(`${label} debe ser una URL válida (https://…).`);
    }
    const capacity = form.capacity.trim() ? Number(form.capacity) : null;
    if (capacity !== null && (!Number.isInteger(capacity) || capacity < 1)) return setFormError("El cupo debe ser un número entero mayor a 0.");
    const recMin = form.recordingMinutes.trim() ? Number(form.recordingMinutes) : null;

    setSaving(true);
    try {
      await saveEvent({
        id: modal.editing?.id,
        title,
        description: form.description.trim() || null,
        kind: form.kind,
        startsAt: new Date(form.startsLocal).toISOString(),
        durationMinutes: duration,
        hostName: form.host.trim() || null,
        imageUrl: form.imageUrl || null,
        imageVerticalUrl: form.imageVerticalUrl || null,
        imageSquareUrl: form.imageSquareUrl || null,
        joinUrl: form.joinUrl.trim() || null,
        recordingUrl: form.recordingUrl.trim() || null,
        recordingSeconds: recMin && recMin > 0 ? Math.round(recMin * 60) : null,
        capacity,
        audience: form.audience,
        active: form.active,
        cancelled: form.cancelled,
      });
      setModal(null);
      await load();
      showToast("Evento guardado");
    } catch {
      setFormError("No se pudo guardar el evento. Revisa los datos e intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const showAttendees = async (e: CalEvent) => {
    setAttendees({ event: e, list: null });
    try {
      setAttendees({ event: e, list: await fetchAttendees(e.id) });
    } catch {
      setAttendees(null);
      showToast("No se pudo cargar la lista.");
    }
  };

  return (
    <AdminShell title="Calendario" description="Crea masterclass, orientaciones y entrenamientos. Tu equipo los ve en Reuniones, reserva su cupo y los agrega a su calendario.">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading font-bold text-foreground-950">Eventos</h2>
        <button onClick={() => open(null)} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line mr-1"></i>Nuevo evento
        </button>
      </div>

      {loading ? (
        <div className="mt-4 bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : error ? (
        <div className="mt-4 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">{error}</div>
      ) : (
        <div className="mt-4 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
          <ul className="divide-y divide-background-200">
            {events.map((e) => {
              const meta = KIND_META[e.kind];
              const upcoming = isUpcoming(e);
              return (
                <li key={e.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                  {pickImage(e, ["square", "horizontal", "vertical"]) ? (
                    <img src={pickImage(e, ["square", "horizontal", "vertical"]) ?? ""} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0 bg-background-200" />
                  ) : (
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.badge}`}>
                      <i className={`${meta.icon} text-lg`}></i>
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-foreground-950 text-sm truncate">{e.title}</div>
                    <div className="text-xs text-foreground-500">
                      {meta.label} · {new Date(e.startsAt).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" })} · {e.durationMinutes} min
                      {e.audience === "equipo" ? " · Solo equipo" : ""}
                    </div>
                  </div>
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${!e.active ? "bg-background-200 text-foreground-600" : upcoming ? "bg-primary-500/15 text-primary-700" : "bg-background-200 text-foreground-600"}`}>
                    {e.cancelled ? "Cancelado" : !e.active ? "Oculto" : upcoming ? "Próximo" : "Finalizado"}
                  </span>
                  <button onClick={() => showAttendees(e)} className="text-xs font-semibold text-foreground-700 px-3 py-1.5 rounded-full border border-background-300 hover:bg-background-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-group-line mr-1"></i>
                    {e.registered}
                    {e.capacity ? `/${e.capacity}` : ""} reservas
                  </button>
                  <button onClick={() => open(e)} aria-label="Editar evento" className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer">
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`¿Eliminar el evento "${e.title}"? También se borrarán sus reservas.`)) return;
                      await deleteEvent(e.id).catch(() => showToast("No se pudo eliminar."));
                      await load();
                    }}
                    aria-label="Eliminar evento"
                    className="w-8 h-8 rounded-full text-foreground-500 hover:text-accent-600 hover:bg-background-200 cursor-pointer"
                  >
                    <i className="ri-delete-bin-6-line"></i>
                  </button>
                </li>
              );
            })}
            {events.length === 0 ? <li className="p-8 text-center text-sm text-foreground-500">Aún no hay eventos. Crea el primero.</li> : null}
          </ul>
        </div>
      )}

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setModal(null)}></div>
          <div className="relative w-full max-w-xl max-h-[calc(100dvh-2rem)] overflow-y-auto bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">{modal.editing ? "Editar evento" : "Nuevo evento"}</h3>
            <div className="mt-4 space-y-3">
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Título del evento" maxLength={120} className={inputCls} />
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descripción (opcional)" rows={3} maxLength={1000} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-semibold text-foreground-600">
                  Tipo
                  <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as EventKind }))} className={`${inputCls} mt-1 cursor-pointer`}>
                    {(Object.keys(KIND_META) as EventKind[]).map((k) => (
                      <option key={k} value={k}>
                        {KIND_META[k].label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-semibold text-foreground-600">
                  Quién lo ve
                  <select value={form.audience} onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value as EventAudience }))} className={`${inputCls} mt-1 cursor-pointer`}>
                    <option value="todos">Todos (incluye invitados)</option>
                    <option value="equipo">Solo socios y equipo</option>
                  </select>
                </label>
                <label className="block text-xs font-semibold text-foreground-600">
                  Fecha y hora (tu zona horaria)
                  <input type="datetime-local" value={form.startsLocal} onChange={(e) => setForm((f) => ({ ...f, startsLocal: e.target.value }))} className={`${inputCls} mt-1`} />
                </label>
                <label className="block text-xs font-semibold text-foreground-600">
                  Duración (min)
                  <input value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" className={`${inputCls} mt-1`} />
                </label>
              </div>
              <input value={form.host} onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))} placeholder="Presenta (ej. Laura Gutiérrez)" maxLength={80} className={inputCls} />
              <input value={form.joinUrl} onChange={(e) => setForm((f) => ({ ...f, joinUrl: e.target.value }))} placeholder="Enlace para unirse (Zoom, Meet…)" className={inputCls} />
              <EventImageSlot
                value={form.imageSquareUrl || form.imageUrl || form.imageVerticalUrl || null}
                onChange={(u, slot) =>
                  setForm((f) => ({
                    ...f,
                    imageUrl: slot === "horizontal" && u ? u : "",
                    imageVerticalUrl: slot === "vertical" && u ? u : "",
                    imageSquareUrl: slot === "square" && u ? u : "",
                  }))
                }
              />
              <div className="grid grid-cols-[1fr_8rem] gap-3">
                <input value={form.recordingUrl} onChange={(e) => setForm((f) => ({ ...f, recordingUrl: e.target.value }))} placeholder="Enlace de la grabación (opcional)" className={inputCls} />
                <input value={form.recordingMinutes} onChange={(e) => setForm((f) => ({ ...f, recordingMinutes: e.target.value.replace(/\D/g, "") }))} placeholder="Min. grab." inputMode="numeric" className={inputCls} />
              </div>
              <label className="block text-xs font-semibold text-foreground-600">
                Cupo máximo (déjalo vacío si no hay límite)
                <input value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" className={`${inputCls} mt-1`} />
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="w-4 h-4 accent-primary-500" />
                Publicado (visible para el equipo)
              </label>
              {modal.editing ? (
                <label className="flex items-start gap-2 text-sm text-foreground-700 cursor-pointer">
                  <input type="checkbox" checked={form.cancelled} onChange={(e) => setForm((f) => ({ ...f, cancelled: e.target.checked }))} className="w-4 h-4 mt-0.5 accent-primary-500" />
                  <span>
                    Evento cancelado
                    <span className="block text-xs text-foreground-500">Se les avisa por WhatsApp a quienes ya reservaron y activaron los avisos.</span>
                  </span>
                </label>
              ) : null}
            </div>
            {formError ? <p className="mt-3 text-sm text-accent-700">{formError}</p> : null}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">
                Cancelar
              </button>
              <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer">
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {attendees ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setAttendees(null)}></div>
          <div className="relative w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto bg-background-50 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-bold text-foreground-950">Reservas</h3>
                <p className="text-xs text-foreground-500">{attendees.event.title}</p>
              </div>
              <button onClick={() => setAttendees(null)} aria-label="Cerrar" className="w-8 h-8 rounded-full hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {attendees.list === null ? (
              <div className="py-8 text-center text-foreground-500">
                <i className="ri-loader-4-line animate-spin text-2xl"></i>
              </div>
            ) : attendees.list.length === 0 ? (
              <p className="mt-4 text-sm text-foreground-500">Todavía nadie ha reservado.</p>
            ) : (
              <ul className="mt-4 divide-y divide-background-200">
                {attendees.list.map((a) => (
                  <li key={a.userId} className="py-2.5">
                    <div className="text-sm font-semibold text-foreground-950">{a.fullName ?? "Sin nombre"}</div>
                    <div className="text-xs text-foreground-500">{a.email}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}

      {toast ? <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AdminShell>
  );
}
